import {
  AgropilotPermissionManagerService,
  API_URL,
  AuthService,
  ErrorInterceptor,
  LoggerService,
  MonitoringService,
  provideTranslations,
  SharedConstantService,
  SharedPermissionService,
  SharedUserService,
  StandaloneTokenInterceptor,
  TokenInitializationService,
  VersionService,
  WithCredentialsInterceptor,
} from '@agroone-front/shared'
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http'
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  Injector,
  LOCALE_ID,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core'
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async'
import { PreloadAllModules, provideRouter, withComponentInputBinding, withPreloading } from '@angular/router'
import { provideServiceWorker } from '@angular/service-worker'
import { environment } from '@front-app-environments/environment'
import { provideEffects } from '@ngrx/effects'
import { provideStore, ReducerManager, Store } from '@ngrx/store'
import { provideStoreDevtools } from '@ngrx/store-devtools'
import { TranslateService } from '@ngx-translate/core'
import { init } from './app-init-factory'
import { appRoutes } from './app.routes'
import { AppService } from './app.service'
import { AppInjector } from './core/app-injector'
import { GlobalErrorHandler } from './core/message/services/error-handler.service'
import { OfflineInterceptor } from './core/network/offline.interceptor'
import { INDEXDB_CONFIG } from './core/storage/indexdb.config'
import { PermissionService } from './core/user/permission.service'
import { UserService } from './core/user/user.service'
import { ActivitiesEffects } from './scene/crop-management/crop/store/effects/activity.effects'
import { CropEffects } from './scene/crop-management/crop/store/effects/crop.effects'
import { InterventionEffects } from './scene/crop-management/crop/store/effects/intervention.effects'
import { OfflineEffects } from './scene/crop-management/crop/store/effects/offline.effects'
import { RecommendationEffects } from './scene/crop-management/crop/store/effects/recommendation.effects'
import { ScoutingEffects } from './scene/crop-management/crop/store/effects/scouting.effects'
import { SpecificationEffects } from './scene/crop-management/crop/store/effects/specification.effects'
import { VarietyEffects } from './scene/crop-management/crop/store/effects/variety.effects'
import { VarietyState } from './scene/crop-management/crop/store/reducers'
import { SpecificationState } from './scene/crop-management/crop/store/reducers/specification.reducer'
import { FarmEffects } from './scene/crop-management/farm/store/effects/farm.effects'
import { FieldEffects } from './scene/crop-management/farm/store/effects/field.effects'
import { FarmState } from './scene/crop-management/farm/store/reducers'
import { ConstantService } from './shared/constant/constant.service'
import { CropService } from './shared/crop/services/crop.service'
import { LanguageService } from './shared/languages/languages.service'
import { RegionService } from './shared/region/services/region.service'
import { SettingsService } from './shared/settings/settings.service'
import { idbReducer, metaReducers } from './state/app.reducer'
import { IdbStorageService } from './state/idb-storage.service'
import { loadingReducer } from './state/loading/loading.reducer'
import { TranslationsLoader } from './translations.loader'

export const DEFAULT = {
  dbName: 'default',
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(appRoutes, withPreloading(PreloadAllModules), withComponentInputBinding()),
    provideTranslations(TranslationsLoader),
    provideStore(
      { loading: loadingReducer },
      {
        metaReducers,
        runtimeChecks: {
          strictStateImmutability: true,
          strictActionImmutability: true,
          strictStateSerializability: false,
          strictActionSerializability: false,
          strictActionTypeUniqueness: true,
        },
      },
    ),
    provideStoreDevtools(),
    provideEffects([
      FarmEffects,
      FieldEffects,
      CropEffects,
      ScoutingEffects,
      InterventionEffects,
      RecommendationEffects,
      ActivitiesEffects,
      OfflineEffects,
      VarietyEffects,
      SpecificationEffects,
    ]),
    provideServiceWorker('ngsw-worker.js', {
      enabled: !environment.local,
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideAnimationsAsync(),
    // Initializers
    {
      provide: SharedUserService,
      useClass: UserService,
    },
    {
      provide: SharedPermissionService,
      useClass: PermissionService,
    },
    // Replaces the AppModule constructor, which published the root injector before any initializer ran.
    provideAppInitializer(() => {
      AppInjector.setInjector(inject(Injector))
    }),
    provideAppInitializer(() => {
      const initializerFn = idbReducer(inject(ReducerManager), inject(IdbStorageService))
      return initializerFn()
    }),
    provideAppInitializer(() => {
      const initializerFn = init(
        inject(SharedUserService),
        inject(ConstantService),
        inject(SettingsService),
        inject(RegionService),
        inject(LanguageService),
        inject(Store<FarmState>),
        inject(CropService),
        inject(LoggerService),
        inject(AuthService),
        inject(Store<VarietyState>),
        inject(Store<SpecificationState>),
        inject(TokenInitializationService),
        inject(AppService),
      )
      return initializerFn()
    }),
    // Error handling
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    // Interceptors
    {
      provide: HTTP_INTERCEPTORS,
      useClass: WithCredentialsInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: OfflineInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: StandaloneTokenInterceptor,
      multi: true,
    },
    {
      provide: API_URL,
      useValue: environment.apiUrl,
    },
    // Dates
    { provide: INDEXDB_CONFIG, useValue: DEFAULT },
    {
      provide: LOCALE_ID,
      deps: [TranslateService],
      useFactory: localeFactory,
    },
    MonitoringService,
    LoggerService,
    AuthService,
    VersionService,
    ConstantService,
    {
      provide: SharedConstantService,
      useExisting: ConstantService,
    },
    AgropilotPermissionManagerService,
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
  ],
}

export function localeFactory(translate: TranslateService): string {
  const lang =
    translate.getCurrentLang() ||
    translate.getFallbackLang() ||
    localStorage.getItem('userLanguage') ||
    window.navigator.language ||
    'en-us'
  // Angular's built-in locale data for some languages is registered under a short code
  const localeMap: Record<string, string> = { 'pl-pl': 'pl', 'hu-hu': 'hu' }
  return localeMap[lang] ?? lang
}
